/**
 * EL CATÁLOGO DE CUENTAS, CONTESTADO POR EL CONTADOR — sin cuenta, con el enlace del asesor.
 *
 * Es la misma pantalla que usa el asesor en la cotización (`CatalogoContador`), con otro servicio
 * y sin lo que solo le toca a él: completar la empresa ya creada y emitir el enlace. Así un
 * arreglo en los pasos llega a los dos a la vez.
 */

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Loader2, XCircle } from 'lucide-react'
import { Card } from '@/components/ui/card'
import CatalogoContador from '@/components/cd2b/CatalogoContador'
import servicioPublico from '@/services/cd2bCatalogoContadorPublicoService'
import { mensajeDeError } from '@/lib/errores'

const fecha = (d) =>
  d ? new Date(d).toLocaleDateString('es-419', { day: 'numeric', month: 'long', year: 'numeric' }) : ''

function Aviso({ icono, titulo, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      <Card className="w-full max-w-md p-6 sm:p-8 text-center">
        {icono}
        <h1 className="font-display text-h2 font-semibold text-foreground">{titulo}</h1>
        <p className="text-body-sm text-subtle mt-2">{children}</p>
      </Card>
    </div>
  )
}

export default function CatalogoContadorPublico() {
  const { token } = useParams()
  const [inicio, setInicio] = useState({ cargando: true })

  // Se valida el enlace ANTES de pintar los pasos: con un enlace vencido la pantalla quedaba en
  // blanco, porque sin estado no hay ningún paso que mostrar.
  useEffect(() => {
    let cancelado = false
    ;(async () => {
      try {
        const r = await servicioPublico.estadoCatalogoContador(token)
        if (!cancelado) setInicio({ empresa: r.data?.empresa, venceAt: r.data?.venceAt })
      } catch (e) {
        if (!cancelado) {
          setInicio({
            error: mensajeDeError(e, 'No se pudo abrir el enlace.'),
            vencido: e?.response?.status === 410,
          })
        }
      }
    })()
    return () => {
      cancelado = true
    }
  }, [token])

  if (inicio.cargando) {
    return (
      <Aviso icono={<Loader2 size={28} className="mx-auto animate-spin text-primary mb-3" />} titulo="Abriendo el catálogo…">
        Un momento.
      </Aviso>
    )
  }

  if (inicio.error) {
    return (
      <Aviso
        icono={<XCircle size={40} className="mx-auto text-danger mb-3" />}
        titulo={inicio.vencido ? 'El enlace venció' : 'El enlace no abre'}
      >
        {inicio.error}
      </Aviso>
    )
  }

  return (
    <div className="min-h-screen bg-background px-4 py-6 sm:py-10">
      <div className="mx-auto w-full max-w-4xl space-y-5">
        <header className="space-y-2">
          <p className="text-caption uppercase tracking-wide text-subtle">
            cd-2b · Catálogo de cuentas
          </p>
          <h1 className="font-display text-h2 font-semibold text-foreground text-balance">
            {inicio.empresa ? `El catálogo de ${inicio.empresa}` : 'El catálogo de cuentas de su cliente'}
          </h1>
          <p className="max-w-prose text-body-sm text-subtle">
            El asesor va a crear la empresa en cd-2b con el catálogo que usted lleva. Para que cada cuenta entre con su
            significado, hacen falta cuatro cosas: el archivo, qué es cada grupo de su numeración, cuáles son las
            cuentas clave y quién lo autorizó. Todo se guarda al elegirlo; puede dejarlo y volver con este mismo enlace
            {inicio.venceAt ? ` hasta el ${fecha(inicio.venceAt)}` : ''}.
          </p>
        </header>
        <CatalogoContador propuestaId={token} servicio={servicioPublico} publico />
      </div>
    </div>
  )
}
